import { Form, Formik, useFormikContext } from "formik";
import React, { FC, useCallback, useEffect, useState } from "react";
import Modal from 'react-modal';
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { DeviceTimePattern } from "../../../models/DeviceTimePattern";
import Wizard from "../../wizard/wizard";
import { WizardSummaryContext } from "../../wizard/wizardSummaryContext";
import { WizardContent } from "./wizardContent";
import { WizardDescription } from "./wizardDescription";
import { WizardSummary } from "./wizardSummary";

interface DataFilterWizardProps {
    isOpen: boolean;
    title: string;
    onClose: () => void;
    onSave: (dataFilters, createNew) => void;
    currentItem: any,
    profileType: string,
    presetType: string
}

const defaultDeviceTimePattern = {
    days: '',
    id: 0,
    startTime: '--:--',
    endTime: '--:--',
    endDate: '----',
    startDate: '----',
    site: '',
    name: '',
    duration: '',
}

const defaultMode = {
    id: 0,
    name: '',
    settingTypeId: '',
}

export const PresetDeviceScheduleWizard: FC<DataFilterWizardProps> = ({ isOpen, title, onClose, onSave, currentItem, profileType, presetType }) => {
    const [summary, setSummary] = useState(new Array(0));
    const [step, setStep] = useState(0);
    const { translate } = useLocale();
    const [selectedDeviceTimePattern, setSelectedDeviceTimePattern] = useState<DeviceTimePattern>(defaultDeviceTimePattern);
    const [selectedMode, setSelectedMode] = useState<any>(defaultMode);
    const { values } = useFormikContext<any>();

    const initialSidebarSteps = [{
        title: translate.byKey(TranslationKeys.mode),
        description: '',
        substeps: []
    }, {
        title: translate.byKey(TranslationKeys.activePeriod),
        description: '',
        substeps: []
    }, {
        title: translate.byKey(TranslationKeys.summary),
        description: '',
        substeps: []
    }];
    const [sidebarSteps, setSidebarSteps] = useState(initialSidebarSteps);

    useEffect(() => {
        (async () => {
            setSummary(new Array(0));
        })();
    }, [isOpen === true])

    const handleClose = () => {
        setSidebarSteps(initialSidebarSteps);
        setStep(0);
        setSelectedDeviceTimePattern(defaultDeviceTimePattern);
        setSelectedMode(defaultMode)
        onClose();
    }

    const handleSubmit = (deviceSchedule) => {
        onSave(deviceSchedule, true);
        handleClose();
    };

    const setSideBarStepDescription = (index, event) => {
        if (event && event.type === "click") {
            // This is for when you are clicking next
            const newSideBarSteps = sidebarSteps.map((step, stepIndex) => {
                return { ...step, description: stepIndex === index ?  event.itemName : step.description };
            });
            setSidebarSteps(newSideBarSteps);
            setStep(step + 1);
        }
        else {
            // This is the initial load with existing data.
            // Needs to be refactor by adding a new prop to the wizards specifically to populate existing data. Not priority.
            sidebarSteps[index].description = event.itemName;
            const newSideBarSteps = [...sidebarSteps];
            setSidebarSteps(newSideBarSteps);
        }
    }

    const contentPages = [
        <WizardDescription
            key={"wizard-1"}
            onBack={handleClose}
            selectedMode={selectedMode}
            setSelectedMode={setSelectedMode}
            profileType={profileType}
            presetType={presetType}
            onNext={(event) => setSideBarStepDescription(0,event)}>
        </WizardDescription>,
        <WizardContent
            key={"wizard-2"}
            selectedDeviceTimePattern={selectedDeviceTimePattern}
            setSelectedDeviceTimePattern={setSelectedDeviceTimePattern}
            onNext={(event) => setSideBarStepDescription(1,event)}
            onBack={() => setStep(step - 1)}
            site = {values.siteId}>
        </WizardContent>,
        <WizardSummary
            key={"wizard-3"}
            selectedDeviceTimePattern={selectedDeviceTimePattern}
            selectedMode={selectedMode}
            onBack={() => setStep(step - 1)}>
        </WizardSummary>
    ];

    const updateSummary = (accessor: string, newDisplayValue: string) => {
        if (summary) {
            const existingSummaryEntryIndex = summary.findIndex(item => item.value === accessor);
            const newSummaryEntry = { value: accessor, label: newDisplayValue };
            if (existingSummaryEntryIndex >= 0) {
                summary.splice(existingSummaryEntryIndex, 1, newSummaryEntry);
            }
            else {
                summary.push(newSummaryEntry);
            }
        }
    }

    const getContextValue = useCallback(
        () => (
            {
                summary: [...summary],
                updateSummary
            }
        ),
        [summary, setSummary],
    )

    return <WizardSummaryContext.Provider value={getContextValue()}>
        <Modal
            isOpen={isOpen}
            ariaHideApp={false}
            overlayClassName="flex w-screen position-float-modal fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
            className="bg-white border-none m-auto shadow select-none outline-none w-content">
            <Formik
                initialValues={currentItem}
                onSubmit={handleSubmit}
                enableReinitialize={true}>
                <Form>
                    <Wizard
                        name={title}
                        onClose={handleClose}
                        step={step}
                        sidebarSteps={sidebarSteps}>
                        {contentPages}
                    </Wizard>
                </Form>
            </Formik>
        </Modal>
    </WizardSummaryContext.Provider>;
};
